import { useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api.js";
import { Skeleton, ComingSoonModal, MobilePreviewModal, OgPreviewModal } from "@templestay/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { I18nEditor, EMPTY_I18N } from "@/components/I18nEditor.js";
import { DefaultLanguagePicker } from "@/components/DefaultLanguagePicker.js";
import { CardBlockEditor } from "@/components/CardBlockEditor.js";
import { InlineCompletion } from "@/components/CompletionBadge.js";
import { useToast } from "@/components/Toast.js";
import { computeI18nCompletion } from "@/lib/i18nCompletion.js";
import type { MainPage, MainPageBlock } from "@templestay/db";
import type { Language } from "@templestay/i18n";

type BlockType = "image" | "carousel" | "map" | "video" | "text" | "cta" | "spacing" | "card";

const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  image: "이미지",
  carousel: "캐러셀",
  map: "지도",
  video: "영상 (구현 예정)",
  text: "텍스트",
  cta: "CTA 버튼",
  spacing: "스페이싱",
  card: "카드",
};

const I18N_FIELDS: Record<BlockType, { key: string; label: string; multiline?: boolean }[]> = {
  image: [{ key: "alt", label: "이미지 설명 (alt)" }],
  carousel: [],
  map: [
    { key: "map_title", label: "지도 버튼 레이블" },
    { key: "modal_desc", label: "모달 설명 (구현 예정 안내)", multiline: true },
  ],
  video: [{ key: "title", label: "영상 제목" }],
  text: [
    { key: "title", label: "제목" },
    { key: "body", label: "본문", multiline: true },
  ],
  cta: [{ key: "label", label: "버튼 레이블" }],
  spacing: [],
  card: [
    { key: "title", label: "제목" },
    { key: "body", label: "본문", multiline: true },
    { key: "annotation", label: "주석" },
    { key: "cta_label", label: "CTA 버튼 레이블" },
  ],
};

const PUBLIC_BASE = import.meta.env.DEV ? "http://localhost:5173" : "https://bongeunsa.app";

export function MainPageEditPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [showOgPreview, setShowOgPreview] = useState(false);
  const [addingType, setAddingType] = useState<BlockType | null>(null);
  const [editingBlock, setEditingBlock] = useState<MainPageBlock | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MainPageBlock | null>(null);
  const [videoComingSoon, setVideoComingSoon] = useState(false);
  const [defaultLang, setDefaultLang] = useState<Language>("ko");
  const { show: showToast, ToastUI } = useToast();
  // 저장/삭제/드래그 후 iframe을 자동으로 새로고침하기 위한 카운터
  const [previewKey, setPreviewKey] = useState(0);
  function refreshPreview() { setPreviewKey((k) => k + 1); }

  // Draft state for new/editing block
  const [draftConfig, setDraftConfig] = useState<Record<string, string>>({});
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);

  // Only track ordered IDs locally — actual block data always comes from server
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ["main-page-edit", id],
    queryFn: async () => {
      const result = await api.get<{ page: MainPage; blocks: MainPageBlock[] }>(`/api/main-pages/${id}`);
      setDefaultLang((result.page.default_language as Language) || "ko");
      return result;
    },
  });

  const sensors = useSensors(useSensor(PointerSensor));

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put(`/api/main-pages/${id}/blocks/reorder`, { ordered_ids: orderedIds }),
    onSuccess: () => {
      refreshPreview();
      // 로컬 순서가 이미 정확하므로 refetch 불필요
    },
    onError: () => {
      setLocalOrder(null); // 오류 시 서버 순서로 복원
      showToast("순서 변경에 실패했습니다.", "error");
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      block_type: string;
      order_index: number;
      config: Record<string, string>;
      i18n: typeof EMPTY_I18N;
    }) => api.post(`/api/main-pages/${id}/blocks`, payload),
    onSuccess: () => {
      setLocalOrder(null);
      qc.invalidateQueries({ queryKey: ["main-page-edit", id] });
      setAddingType(null);
      refreshPreview();
      showToast("블록이 추가되었습니다.");
    },
    onError: () => showToast("블록 추가에 실패했습니다.", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      blockId,
      payload,
    }: {
      blockId: string;
      payload: { config?: Record<string, string>; i18n?: typeof EMPTY_I18N };
    }) => api.put(`/api/main-pages/${id}/blocks/${blockId}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["main-page-edit", id] });
      setEditingBlock(null);
      refreshPreview();
      showToast("블록이 저장되었습니다.");
    },
    onError: () => showToast("블록 저장에 실패했습니다.", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (blockId: string) =>
      api.delete(`/api/main-pages/${id}/blocks/${blockId}`),
    onSuccess: () => {
      setLocalOrder(null);
      qc.invalidateQueries({ queryKey: ["main-page-edit", id] });
      setDeleteTarget(null);
      refreshPreview();
      showToast("블록이 삭제되었습니다.");
    },
    onError: () => showToast("블록 삭제에 실패했습니다.", "error"),
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = blocks.map((b) => b.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    const reordered = arrayMove(ids, oldIndex, newIndex);
    setLocalOrder(reordered);
    clearTimeout(dropTimerRef.current);
    setJustDroppedId(active.id as string);
    dropTimerRef.current = setTimeout(() => setJustDroppedId(null), 400);
    reorderMutation.mutate(reordered);
  }

  function openAdd(type: BlockType) {
    if (type === "video") { setVideoComingSoon(true); return; }
    setAddingType(type);
    setDraftConfig({});
    setDraftI18n(EMPTY_I18N);
  }

  function openEdit(block: MainPageBlock) {
    setEditingBlock(block);
    setDraftConfig(JSON.parse(block.config) as Record<string, string>);
    setDraftI18n(JSON.parse(block.i18n) as typeof EMPTY_I18N);
  }

  function submitAdd() {
    if (!addingType || !data) return;
    createMutation.mutate({
      block_type: addingType,
      order_index: data.blocks.length,
      config: draftConfig,
      i18n: draftI18n,
    });
  }

  function submitEdit() {
    if (!editingBlock) return;
    updateMutation.mutate({
      blockId: editingBlock.id,
      payload: { config: draftConfig, i18n: draftI18n },
    });
  }

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  const blocks = (() => {
    const serverBlocks = data?.blocks ?? [];
    if (!localOrder) return serverBlocks;
    const map = new Map(serverBlocks.map((b) => [b.id, b]));
    return localOrder.map((id) => map.get(id)).filter(Boolean) as MainPageBlock[];
  })();

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {ToastUI}
      {/* ── Left: editor ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <Link to="/main-pages" className="text-sm text-gray-400 hover:text-gray-600">
            ← 목록
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">{data?.page.name}</h1>
          {data?.page.is_active ? (
            <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
              액티브
            </span>
          ) : null}
          <div className="ml-auto flex gap-2 lg:hidden">
            <button
              onClick={() => setShowMobilePreview(true)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              📱 미리보기
            </button>
          </div>
          <button
            onClick={() => setShowOgPreview(true)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 ml-auto lg:ml-0"
          >
            🖼 OG
          </button>
        </div>

        {/* Default language */}
        <div className="mb-4 flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
          <DefaultLanguagePicker value={defaultLang} onChange={setDefaultLang} />
          {defaultLang !== (data?.page.default_language ?? "ko") && (
            <button
              onClick={() => {
                api.put(`/api/main-pages/${id}`, { default_language: defaultLang })
                  .then(() => { qc.invalidateQueries({ queryKey: ["main-page-edit", id] }); showToast("저장되었습니다."); })
                  .catch(() => showToast("오류가 발생했습니다.", "error"));
              }}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
            >
              저장
            </button>
          )}
        </div>

        {/* Add block buttons */}
        <div className="mb-4 flex flex-wrap gap-2">
          {(Object.keys(BLOCK_TYPE_LABELS) as BlockType[]).map((type) => (
            <button
              key={type}
              onClick={() => openAdd(type)}
              className="rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-brand-400 hover:text-brand-600"
            >
              + {BLOCK_TYPE_LABELS[type]}
            </button>
          ))}
        </div>

        {/* Block list */}
        {blocks.length === 0 ? (
          <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-gray-400">블록이 없습니다. 위에서 블록을 추가하세요.</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {blocks.map((block) => (
                  <SortableBlock
                    key={block.id}
                    block={block}
                    isHighlighted={justDroppedId === block.id}
                    onEdit={() => openEdit(block)}
                    onDelete={() => setDeleteTarget(block)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* ── Right: inline mobile preview (lg+) ───────────────── */}
      <div className="hidden lg:flex w-[440px] shrink-0 flex-col items-center gap-4 border-l border-gray-100 bg-gray-50 p-6">
        <div className="flex w-full items-center justify-between">
          <p className="text-xs font-semibold text-gray-400">모바일 미리보기</p>
          <button
            onClick={refreshPreview}
            className="rounded-lg px-2 py-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ↺ 새로고침
          </button>
        </div>
        <div className="overflow-hidden rounded-[2.5rem] shadow-2xl ring-1 ring-black/10"
          style={{ width: 375, height: 667 }}>
          <iframe
            key={previewKey}
            src={`${PUBLIC_BASE}/?preview=${id}`}
            width={375}
            height={667}
            className="block"
            title="모바일 미리보기"
          />
        </div>
      </div>

      {/* Add modal */}
      {addingType && (
        <BlockEditModal
          title={`${BLOCK_TYPE_LABELS[addingType]} 블록 추가`}
          blockType={addingType}
          config={draftConfig}
          i18n={draftI18n}
          onConfigChange={setDraftConfig}
          onI18nChange={setDraftI18n}
          onClose={() => setAddingType(null)}
          onSubmit={submitAdd}
          loading={createMutation.isPending}
        />
      )}

      {/* Edit modal */}
      {editingBlock && (
        <BlockEditModal
          title={`${BLOCK_TYPE_LABELS[editingBlock.block_type as BlockType]} 블록 편집`}
          blockType={editingBlock.block_type as BlockType}
          config={draftConfig}
          i18n={draftI18n}
          onConfigChange={setDraftConfig}
          onI18nChange={setDraftI18n}
          onClose={() => setEditingBlock(null)}
          onSubmit={submitEdit}
          loading={updateMutation.isPending}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="블록 삭제"
        description={`"${deleteTarget?.block_type}" 블록을 삭제할까요? 이 작업은 되돌릴 수 없습니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      <ComingSoonModal
        open={videoComingSoon}
        onClose={() => setVideoComingSoon(false)}
        lang="ko"
        description="영상 임베드 블록은 본 개발에서 제공됩니다."
      />

      <MobilePreviewModal
        open={showMobilePreview}
        onClose={() => setShowMobilePreview(false)}
        title={`${data?.page.name ?? ""} — 모바일 미리보기`}
        src={`${PUBLIC_BASE}/?preview=${id}`}
      />

      <OgPreviewModal
        open={showOgPreview}
        onClose={() => setShowOgPreview(false)}
        title={data?.page.name ?? "봉은사"}
        description="봉은사 공식 다국어 안내 사이트"
        siteName="bongeunsa.app"
        pageUrl={`${PUBLIC_BASE}/?preview=${id}`}
      />
    </div>
  );
}

// ── Image Upload Field ────────────────────────────────────────

function ImageUploadField({
  value,
  onChange,
}: {
  value: string;
  onChange: (url: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.postForm<{ url: string }>("/api/admin/upload", form);
      onChange(res.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "업로드 실패");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      <label className="mb-1 block text-xs font-medium text-gray-500">이미지</label>

      {/* Preview */}
      {value && (
        <img
          src={value}
          alt="미리보기"
          className="h-32 w-full rounded-lg object-cover ring-1 ring-black/10"
        />
      )}

      {/* Upload button */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-lg border border-dashed border-brand-400 px-3 py-2 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50"
        >
          {uploading ? "업로드 중..." : "파일 업로드"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      {/* URL input (manual or auto-filled) */}
      <input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://... (직접 입력 또는 업로드 후 자동 입력)"
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
      />

      {uploadError && (
        <p className="text-xs text-red-500">{uploadError}</p>
      )}
    </div>
  );
}

// ── Carousel Config Editor ────────────────────────────────────

function CarouselConfigEditor({
  images,
  onChange,
}: {
  images: string[];
  onChange: (images: string[]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.postForm<{ url: string }>("/api/admin/upload", form);
      onChange([...images, res.url]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "업로드 실패");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function updateUrl(idx: number, val: string) {
    const next = [...images];
    next[idx] = val;
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs font-medium text-gray-500">캐러셀 이미지</label>
      {images.map((url, idx) => (
        <div key={idx} className="flex gap-2 items-start">
          {url && (
            <img src={url} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0 ring-1 ring-black/10" />
          )}
          <input
            type="url"
            value={url}
            onChange={(e) => updateUrl(idx, e.target.value)}
            placeholder="https://..."
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
          />
          <button
            type="button"
            onClick={() => onChange(images.filter((_, i) => i !== idx))}
            className="shrink-0 text-red-400 hover:text-red-600 px-1 py-2 text-sm"
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange([...images, ""])}
          className="rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-medium text-gray-500 hover:border-brand-400 hover:text-brand-600"
        >
          + URL 직접 입력
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-lg border border-dashed border-brand-400 px-3 py-2 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50"
        >
          {uploading ? "업로드 중..." : "+ 파일 업로드"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
      {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}
    </div>
  );
}

// ── Sortable Block Row ────────────────────────────────────────

function SortableBlock({
  block,
  onEdit,
  onDelete,
  isHighlighted,
}: {
  block: MainPageBlock;
  onEdit: () => void;
  onDelete: () => void;
  isHighlighted?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  const i18n = JSON.parse(block.i18n) as Record<string, Record<string, string>>;
  const koText = i18n.ko ?? {};
  const preview =
    koText.title ?? koText.label ?? koText.alt ?? block.block_type;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div
        className={`flex items-center gap-3 rounded-xl bg-white px-4 py-3 shadow-sm transition-shadow duration-300 ${
          isDragging ? "ring-2 ring-brand-300 shadow-lg" : isHighlighted ? "ring-2 ring-brand-400" : "ring-1 ring-black/5"
        }`}
      >
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-gray-300 hover:text-gray-500 active:cursor-grabbing"
          aria-label="순서 변경"
        >
          ⠿
        </button>

        <div className="flex-1 min-w-0">
          <span className="mr-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
            {BLOCK_TYPE_LABELS[block.block_type as BlockType] ?? block.block_type}
          </span>
          <span className="text-sm text-gray-700 truncate">{preview}</span>
          {I18N_FIELDS[block.block_type as BlockType]?.length > 0 && (
            <BlockCompletionBadge i18nStr={block.i18n} blockType={block.block_type as BlockType} />
          )}
        </div>

        <button
          onClick={onEdit}
          className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          편집
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-50"
        >
          삭제
        </button>
      </div>
    </div>
  );
}

// ── Block Edit Modal ──────────────────────────────────────────

function BlockEditModal({
  title,
  blockType,
  config,
  i18n,
  onConfigChange,
  onI18nChange,
  onClose,
  onSubmit,
  loading,
}: {
  title: string;
  blockType: BlockType;
  config: Record<string, string>;
  i18n: typeof EMPTY_I18N;
  onConfigChange: (v: Record<string, string>) => void;
  onI18nChange: (v: typeof EMPTY_I18N) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  const fields = I18N_FIELDS[blockType];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="text-sm font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Config fields */}
          {blockType === "image" && (
            <ImageUploadField
              value={config.url ?? ""}
              onChange={(url) => onConfigChange({ ...config, url })}
            />
          )}

          {blockType === "carousel" && (
            <CarouselConfigEditor
              images={
                Array.isArray(config.images)
                  ? (config.images as string[])
                  : typeof config.images === "string"
                  ? (JSON.parse(config.images) as string[])
                  : []
              }
              onChange={(images) =>
                onConfigChange({ ...config, images: JSON.stringify(images) } as Record<string, string>)
              }
            />
          )}

          {blockType === "map" && (
            <ImageUploadField
              value={config.url ?? ""}
              onChange={(url) => onConfigChange({ ...config, url })}
            />
          )}

          {blockType === "cta" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                링크 URL
              </label>
              <input
                type="text"
                value={config.url ?? ""}
                onChange={(e) => onConfigChange({ ...config, url: e.target.value })}
                placeholder="/survey/... 또는 https://..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
              />
            </div>
          )}

          {blockType === "spacing" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                높이 (px)
              </label>
              <input
                type="number"
                value={config.height ?? "24"}
                onChange={(e) => onConfigChange({ ...config, height: e.target.value })}
                min={8}
                max={200}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
              />
            </div>
          )}

          {/* card block: dedicated editor */}
          {blockType === "card" && (
            <CardBlockEditor
              config={config}
              i18n={i18n}
              onConfigChange={onConfigChange}
              onI18nChange={onI18nChange}
            />
          )}

          {/* i18n editor (non-card blocks) */}
          {blockType !== "card" && fields.length > 0 && (
            <div>
              <label className="mb-2 block text-xs font-medium text-gray-500">
                다국어 텍스트
              </label>
              <I18nEditor
                value={i18n}
                fields={fields}
                onChange={onI18nChange}
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </>
  );
}

function BlockCompletionBadge({ i18nStr, blockType }: { i18nStr: string; blockType: BlockType }) {
  const fields = (I18N_FIELDS[blockType] ?? []).map((f) => f.key);
  if (fields.length === 0) return null;
  const { overall } = computeI18nCompletion(i18nStr, fields);
  return <span className="ml-1"><InlineCompletion pct={overall} /></span>;
}
